/**
 * Color palette for Orbit application
 * Colors are organized by their usage/purpose
 */

export const colors = {
	// Main/Secondary Background Colors
	backgroundOrange: '#fffaf7',
	backgroundGreen: '#ecedeb',
	backgroundBlue: '#ebecf5',

	// Tab and Nav-bar Background
	vanilla: '#faf0f0',

	// Primary Colors (darkOrange has more emphasis)
	darkOrange: '#a03c04',
	orange: '#d15f2a',

	// Cool primary (titles, CTAs paired with warm)
	blue: '#394080',
	blueDark: '#2a3068',

	// Secondary Color (ex. Filter, Add day Note)
	gold: '#ed9247',

	// Highlighted CTA Buttons (ex. Add Expense)
	green: '#3e734d',

	// Success / state colors
	success: '#2e7d4f',
	red: '#f36f6f',

	// Destructive actions and error text (AA on white and on dangerTint). Coral `red` stays a fill: badges, chart series.
	danger: '#d61f1f',
	dangerHover: '#b91c1c',
	dangerTint: '#fef2f2',

	// Title Text Color or Icon Tints
	blackOrange: '#541202',
	blackGreen: '#2e4d37',

	// Normal/Low Emphasis Text
	black: '#262626',
	textMuted: '#5c5c5c',
	// Design-system `--mid-grey`: low-emphasis text, disabled fills.
	darkGrey: '#c7c7c7',
	// Design-system `--dark-grey`: de-emphasised copy that still reads as text.
	mutedGrey: '#a3a3a3',
	// Design-system `--light-grey`: out-of-reach steps.
	lightGrey: '#dcdcdc',

	// Neutral Colors
	white: '#FFFFFF',
	grey: '#f4f4f4',
	// Warm off-white for hover rows and panel fills (replaces the Tailwind / Bootstrap #f3f4f6, #f8f9fa).
	surface2: '#f9f3f0',

	// Warm tint used for the bento icon chips / date chips
	warmTint: '#fce6d6',
	// Timeline rail dash — dots, connectors and chips on the itinerary board and overview tile
	rail: '#d4a574',

	// PDF badge (file thumbnails) — mirrors the mobile apps' PdfBadgeBg/Fg
	pdfBadgeBg: '#fbe5e1',
	pdfBadgeFg: '#b23b2e',

	// Hairline borders / dividers (see design tokens --border-1 / --border-2)
	border1: 'rgba(38, 38, 38, 0.08)',
	border2: 'rgba(38, 38, 38, 0.14)',

	// Design-system dividers: 1px black @10%, or blue @15% above a bottom bar
	divider: 'rgba(38, 38, 38, 0.1)',
	dividerBlue: 'rgba(57, 64, 128, 0.15)',

	// Modal / sheet scrim — warm, never neutral black
	scrim: 'rgba(38, 18, 2, 0.35)',
	// Image lightbox dim — the one place neutral black is right (photos)
	scrimDark: 'rgba(0, 0, 0, 0.85)',

	// Third-party brand marks: the Google "G" and the link-source badges. Never Orbit's own UI.
	googleBlue: '#4285F4',
	googleGreen: '#34A853',
	googleYellow: '#FBBC05',
	googleRed: '#EA4335',
	tiktok: '#000000',
	instagram: '#E4405F',
	appleMaps: '#1d1d1f',
} as const

// Type-safe color keys
export type ColorKey = keyof typeof colors

// Helper function to get a color by key
export const getColor = (key: ColorKey): string => colors[key]

// Export individual colors for convenience
export const {
	backgroundOrange,
	backgroundGreen,
	backgroundBlue,
	vanilla,
	darkOrange,
	orange,
	blue,
	blueDark,
	gold,
	green,
	success,
	red,
	danger,
	dangerHover,
	dangerTint,
	blackOrange,
	blackGreen,
	black,
	textMuted,
	darkGrey,
	mutedGrey,
	lightGrey,
	white,
	grey,
	surface2,
	warmTint,
	rail,
	pdfBadgeBg,
	pdfBadgeFg,
	border1,
	border2,
	divider,
	dividerBlue,
	scrim,
	scrimDark,
	googleBlue,
	googleGreen,
	googleYellow,
	googleRed,
	tiktok,
	instagram,
	appleMaps,
} = colors

